import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonBadge,
  IonSelect,
  IonSelectOption,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  useIonRouter,
} from '@ionic/react';
import {
  chevronBack,
  chevronForward,
  home,
  people,
  peopleOutline,
  map,
  mapOutline,
  calendarOutline,
  locationOutline,
  pencilOutline,
  warningOutline,
  navigateOutline,
} from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useSector, useSectorFamilies, useSectorsGeoJson } from '@/hooks/useSectors';
import { useMapFamilies, usePendingReview } from '@/hooks/useMapFamilies';
import { usePermission } from '@/hooks/usePermission';
import type { Family } from '@/types/family';
import { FAMILY_ROLE_LABELS } from '@/utils/labels';
import SectorPolygonsLayer from '@/components/map/SectorPolygonsLayer';
import SectorMembersGrouped from '@/components/sectors/SectorMembersGrouped';
import { cartoTileUrl } from '@/utils/cartoTiles';

const TILE_URL = cartoTileUrl('rastertiles/voyager');
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

type SortKey = 'name' | 'address' | 'member_count';
const COLUMNS: { key: SortKey; label: string; minWidth?: number }[] = [
  { key: 'name', label: 'Familie', minWidth: 200 },
  { key: 'address', label: 'Adresă', minWidth: 180 },
  { key: 'member_count', label: 'Membri', minWidth: 80 },
];
const PAGE_SIZES = [25, 50, 100] as const;

function useIsMobile(breakpoint = 992) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ── Mini-map family dots (markers only; bounds handled by MiniMapController) ── */
const FamilyDotsLayer: React.FC<{ families: Array<{ latitude: number; longitude: number; name: string }> }> = ({ families }) => {
  const mapInst = useMap();

  useEffect(() => {
    const markers = L.layerGroup();
    families.forEach((f) => {
      if (!f.latitude || !f.longitude) return;
      L.circleMarker([f.latitude, f.longitude], {
        radius: 5,
        fillColor: '#5e81f4',
        fillOpacity: 0.7,
        weight: 1,
        color: '#fff',
        opacity: 0.9,
      }).addTo(markers);
    });
    markers.addTo(mapInst);
    return () => { mapInst.removeLayer(markers); };
  }, [mapInst, families]);

  return null;
};

/* ── Fit bounds once + invalidate size on resize (prevents flicker and grey tiles) ── */
const MiniMapController: React.FC<{
  sectorGeoJson?: GeoJSON.FeatureCollection;
  families: Array<{ latitude: number; longitude: number; name: string }>;
}> = ({ sectorGeoJson, families }) => {
  const mapInst = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    const el = mapInst.getContainer();
    const ro = new ResizeObserver(() => mapInst.invalidateSize());
    ro.observe(el);
    const t = window.setTimeout(() => mapInst.invalidateSize(), 0);
    return () => { ro.disconnect(); window.clearTimeout(t); };
  }, [mapInst]);

  useEffect(() => {
    if (hasFitted.current) return;

    let bounds: L.LatLngBounds | null = null;
    if (sectorGeoJson && sectorGeoJson.features.length > 0) {
      const layer = L.geoJSON(sectorGeoJson);
      const b = layer.getBounds();
      if (b.isValid()) bounds = b;
    }
    if (!bounds) {
      const pts = families
        .filter(f => f.latitude && f.longitude)
        .map(f => [f.latitude, f.longitude] as [number, number]);
      if (pts.length > 0) bounds = L.latLngBounds(pts);
    }

    if (bounds && bounds.isValid()) {
      mapInst.invalidateSize();
      mapInst.fitBounds(bounds, { padding: [30, 30], maxZoom: 15, animate: false });
      hasFitted.current = true;
    }
  }, [mapInst, sectorGeoJson, families]);

  return null;
};

const SectorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useIonRouter();
  const { can } = usePermission();
  const canEdit = can('sectors.edit');
  const isMobile = useIsMobile();
  const { data: sector, isLoading: sectorLoading } = useSector(id);
  const [familyPage, setFamilyPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const { data: familiesData, isLoading: familiesLoading, isFetching: familiesFetching, refetch } = useSectorFamilies(
    id, familyPage, pageSize, sortKey, sortAsc ? 'asc' : 'desc',
  );
  const { data: sectorsGeoJson } = useSectorsGeoJson();
  const { data: mapFamilies } = useMapFamilies({ sector_id: id });
  const { data: pendingFamilies } = usePendingReview(id);

  const families = familiesData?.data ?? [];
  const totalCount = familiesData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const [activeTab, setActiveTab] = useState<'families' | 'grouped'>('families');
  const color = sector?.color || '#4f46e5';

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
    setFamilyPage(1);
  }, [sortKey]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setFamilyPage(1);
  }, []);

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '');

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (familyPage > 3) pages.push('ellipsis');
      const start = Math.max(2, familyPage - 1);
      const end = Math.min(totalPages - 1, familyPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (familyPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, familyPage]);

  // Filter GeoJSON to only this sector
  const sectorGeoJson = sectorsGeoJson && sector ? {
    type: 'FeatureCollection' as const,
    features: sectorsGeoJson.features.filter(f => f.properties?.id === sector.id),
  } : undefined;

  const geocodedCount = mapFamilies?.filter(f => f.latitude && f.longitude).length ?? 0;
  const pendingCount = pendingFamilies?.length ?? 0;
  const pendingIds = useMemo(() => new Set(pendingFamilies?.map(f => f.id) ?? []), [pendingFamilies]);

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="detail-page">
          {/* Top bar */}
          <div className="form-topbar">
            <button className="form-topbar__back" onClick={() => router.push('/sectors', 'back')}>
              <IonIcon icon={chevronBack} /> Sectoare
            </button>
          </div>

          {sectorLoading ? (
            <div style={{ padding: '20px 0' }}>
              <IonSkeletonText animated style={{ width: '40%', height: 28 }} />
              <IonSkeletonText animated style={{ width: '60%', height: 16, marginTop: 12 }} />
            </div>
          ) : sector ? (
            <>
              {/* Header */}
              <div className="detail-header" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 56, height: 56, borderRadius: 14,
                      backgroundColor: `${color}18`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: 22, fontWeight: 800, color }}>{sector.number}</span>
                  </div>
                  <div>
                    <h1 className="detail-header__name">{sector.name}</h1>
                    <div className="detail-header__meta" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                      <span className={`sector-geo-badge ${sector.has_geometry ? 'sector-geo-badge--defined' : 'sector-geo-badge--undefined'}`}>
                        <IonIcon icon={sector.has_geometry ? map : mapOutline} />
                        {sector.has_geometry ? 'Zona definita' : 'Zona nedefinita'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map snippet or empty-state CTA */}
              {sector.has_geometry || (mapFamilies && mapFamilies.length > 0) ? (
                <div
                  className="sector-minimap-wrap"
                  onClick={() => canEdit && router.push(`/sectors/${id}/boundary`)}
                  style={canEdit ? undefined : { cursor: 'default' }}
                >
                  <div className="sector-minimap">
                    <MapContainer
                      center={[46.1866, 21.3123]}
                      zoom={13}
                      style={{ width: '100%', height: '100%' }}
                      scrollWheelZoom={false}
                      dragging={false}
                      zoomControl={false}
                      attributionControl={false}
                    >
                      <TileLayer url={TILE_URL} attribution={TILE_ATTR} subdomains="abcd" maxZoom={20} />
                      {sectorGeoJson && sectorGeoJson.features.length > 0 && (
                        <SectorPolygonsLayer
                          geojson={sectorGeoJson}
                          showLabels={false}
                          interactive={false}
                        />
                      )}
                      {mapFamilies && <FamilyDotsLayer families={mapFamilies} />}
                      <MiniMapController sectorGeoJson={sectorGeoJson} families={mapFamilies ?? []} />
                    </MapContainer>
                  </div>
                  {canEdit && (
                    <div className="sector-minimap-overlay">
                      <span className="sector-minimap-overlay__label">
                        <IonIcon icon={pencilOutline} style={{ fontSize: 15 }} />
                        {sector.has_geometry ? 'Editeaza zona' : 'Deseneaza zona'}
                      </span>
                    </div>
                  )}
                </div>
              ) : canEdit ? (
                <div className="sector-zone-empty" onClick={() => router.push(`/sectors/${id}/boundary`)}>
                  <div className="sector-zone-empty__icon">
                    <IonIcon icon={mapOutline} />
                  </div>
                  <h3 className="sector-zone-empty__title">Zona nedefinita</h3>
                  <p className="sector-zone-empty__text">
                    Deseneaza o zona pe harta pentru a delimita acest sector si a asigna familii automat.
                  </p>
                  <button className="sector-zone-empty__btn">
                    <IonIcon icon={mapOutline} style={{ fontSize: 15 }} />
                    Deseneaza zona
                  </button>
                </div>
              ) : (
                <div className="sector-zone-empty" style={{ cursor: 'default' }}>
                  <div className="sector-zone-empty__icon" style={{ background: '#f1f5f9', color: '#94a3b8' }}>
                    <IonIcon icon={mapOutline} />
                  </div>
                  <h3 className="sector-zone-empty__title">Zona nedefinita</h3>
                  <p className="sector-zone-empty__text">
                    Acest sector nu are o zona definita pe harta.
                  </p>
                </div>
              )}

              {/* Stats row */}
              <div className="sector-stats">
                <div className="sector-stats__card" style={{ '--card-accent': color } as React.CSSProperties}>
                  <div className="sector-stats__icon-wrap" style={{ background: `${color}12` }}>
                    <IonIcon icon={home} style={{ color, fontSize: 20 }} />
                  </div>
                  <div className="sector-stats__value">{sector.family_count}</div>
                  <div className="sector-stats__label">Familii</div>
                </div>

                <div className="sector-stats__card" style={{ '--card-accent': color } as React.CSSProperties}>
                  <div className="sector-stats__icon-wrap" style={{ background: `${color}12` }}>
                    <IonIcon icon={people} style={{ color, fontSize: 20 }} />
                  </div>
                  <div className="sector-stats__value">{sector.member_count}</div>
                  <div className="sector-stats__label">Membri</div>
                </div>

                <div
                  className="sector-stats__card sector-stats__card--action"
                  style={{ '--card-accent': color } as React.CSSProperties}
                  onClick={() => router.push(`/visits?sector_id=${id}&from_date=${new Date().getFullYear()}-01-01`)}
                >
                  <div className="sector-stats__icon-wrap" style={{ background: `${color}12` }}>
                    <IonIcon icon={calendarOutline} style={{ color, fontSize: 20 }} />
                  </div>
                  <div className="sector-stats__value">{sector.visits_this_year ?? 0}</div>
                  <div className="sector-stats__label">Vizite anul acesta</div>
                  <div className="sector-stats__bar">
                    <div className="sector-stats__bar-fill" style={{ width: `${sector.visited_this_month_pct}%`, background: color }} />
                  </div>
                  <div className="sector-stats__sub">{sector.visited_this_month_pct}% vizitate luna aceasta</div>
                </div>

                <div
                  className="sector-stats__card sector-stats__card--action"
                  style={{ '--card-accent': '#0891b2' } as React.CSSProperties}
                  onClick={() => router.push(`/maps?sector_id=${id}`)}
                >
                  <div className="sector-stats__icon-wrap" style={{ background: '#0891b212' }}>
                    <IonIcon icon={locationOutline} style={{ color: '#0891b2', fontSize: 20 }} />
                  </div>
                  <div className="sector-stats__value">
                    {geocodedCount}<span className="sector-stats__value-dim">/{sector.family_count}</span>
                  </div>
                  <div className="sector-stats__label">Localizare</div>
                  <div className="sector-stats__bar">
                    <div className="sector-stats__bar-fill" style={{ width: `${sector.family_count ? Math.round((geocodedCount / sector.family_count) * 100) : 0}%`, background: '#0891b2' }} />
                  </div>
                  {(sector.family_count - geocodedCount > 0 || pendingCount > 0) && (
                    <div className="sector-stats__alerts">
                      {sector.family_count - geocodedCount > 0 && (
                        <span className="sector-stats__pill sector-stats__pill--red">
                          {sector.family_count - geocodedCount} fără coordonate
                        </span>
                      )}
                      {pendingCount > 0 && (
                        <span
                          className="sector-stats__pill sector-stats__pill--amber"
                          onClick={(e) => { e.stopPropagation(); router.push(`/maps/review?sector_id=${id}`); }}
                        >
                          {pendingCount} de verificat &rarr;
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 20 }}>
                {([
                  { key: 'families' as const, label: 'Familii', count: familiesData?.total ?? 0 },
                  { key: 'grouped' as const, label: 'Membri grupati', count: sector.member_count ?? 0 },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '10px 20px', fontSize: 13, fontWeight: 600, border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      color: activeTab === tab.key ? color : '#94a3b8',
                      borderBottom: activeTab === tab.key ? `2px solid ${color}` : '2px solid transparent',
                      marginBottom: -2, transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {tab.label}
                    <span style={{
                      fontSize: 11, padding: '1px 6px', borderRadius: 10,
                      backgroundColor: activeTab === tab.key ? `${color}15` : '#f1f5f9',
                      color: activeTab === tab.key ? color : '#94a3b8',
                    }}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Families tab */}
              {activeTab === 'families' && (
                <>
                  {/* Summary bar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                      {totalCount} {totalCount === 1 ? 'familie' : 'familii'}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>Rânduri:</span>
                      <IonSelect
                        interface="popover"
                        value={pageSize}
                        onIonChange={(e) => handlePageSizeChange(e.detail.value as number)}
                        style={{ fontSize: 12, minWidth: 60, padding: 0 }}
                      >
                        {PAGE_SIZES.map((size) => (
                          <IonSelectOption key={size} value={size}>{size}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </div>
                  </div>

                  {/* Background fetch indicator */}
                  {familiesFetching && familiesData && (
                    <div className="crm-fetch-indicator">
                      <div className="crm-fetch-indicator__bar" />
                    </div>
                  )}

                  {/* Desktop table */}
                  {!isMobile && (
                    <div className="crm-card" style={{ overflow: 'auto' }}>
                      {familiesLoading ? (
                        <div style={{ padding: 20 }}>
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <IonSkeletonText animated style={{ width: '50%', height: 16 }} />
                              <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <table className="crm-table">
                          <thead>
                            <tr>
                              {COLUMNS.map((col) => (
                                <th
                                  key={col.key}
                                  onClick={() => handleSort(col.key)}
                                  style={{ minWidth: col.minWidth, cursor: 'pointer' }}
                                >
                                  {col.label}{sortArrow(col.key)}
                                </th>
                              ))}
                              <th style={{ minWidth: 220 }}>Componenta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {families.map((family: Family) => {
                              const noCoords = !family.latitude || !family.longitude;
                              const hasPending = pendingIds.has(family.id);
                              const hasIssue = noCoords || hasPending;

                              return (
                              <tr
                                key={family.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => router.push(`/families/${family.id}`)}
                                className={`crm-animate-in${hasIssue ? ' sector-family-row--warn' : ''}`}
                              >
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div
                                      style={{
                                        width: 34, height: 34, borderRadius: '50%',
                                        backgroundColor: `${color}20`, color,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                                      }}
                                    >
                                      {(family.name[0] ?? '').toUpperCase()}
                                    </div>
                                    <div className="crm-table__name">{family.display_name ?? family.name}</div>
                                  </div>
                                </td>
                                <td style={{ fontSize: 12, color: '#64748b' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>{[family.address, family.city].filter(Boolean).join(', ') || '\u2014'}</span>
                                    {hasIssue && (
                                      <button
                                        className={`sector-loc-badge${noCoords ? ' sector-loc-badge--red' : ' sector-loc-badge--amber'}`}
                                        title={noCoords ? 'Fără coordonate — click pentru verificare' : 'De verificat — click pentru hartă'}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (noCoords) {
                                            router.push(`/maps/review?sector_id=${id}&family_id=${family.id}`);
                                          } else {
                                            router.push(`/maps?sector_id=${id}&fly=${family.id}`);
                                          }
                                        }}
                                      >
                                        <IonIcon icon={noCoords ? warningOutline : navigateOutline} />
                                        {noCoords ? 'Fără loc.' : 'Verifică'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <IonIcon icon={peopleOutline} color="medium" style={{ fontSize: 14 }} />
                                    <span style={{ fontWeight: 600 }}>{family.member_count ?? family.members?.length ?? 0}</span>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {family.members?.slice(0, 4).map((m) => (
                                      <span
                                        key={m.id}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4,
                                          fontSize: 11, color: '#475569', padding: '2px 8px',
                                          backgroundColor: '#f8fafc', borderRadius: 4,
                                        }}
                                      >
                                        {m.first_name} {m.last_name}
                                        {m.family_role && (
                                          <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                            ({FAMILY_ROLE_LABELS[m.family_role] ?? m.family_role})
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                    {(family.members?.length ?? 0) > 4 && (
                                      <span style={{ fontSize: 11, color: '#94a3b8', padding: '2px 4px' }}>
                                        +{(family.members?.length ?? 0) - 4}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}

                      {!familiesLoading && families.length === 0 && (
                        <div className="crm-empty-state">
                          <div className="crm-empty-state__icon"><IonIcon icon={home} /></div>
                          <h3 className="crm-empty-state__title">Nicio familie</h3>
                          <p className="crm-empty-state__text">Acest sector nu are familii alocate.</p>
                        </div>
                      )}

                      {totalPages > 1 && (
                        <div className="crm-pagination">
                          <button className="crm-pagination__btn" disabled={familyPage === 1} onClick={() => setFamilyPage((p) => p - 1)}>
                            <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                          </button>
                          {pageNumbers.map((p, idx) =>
                            p === 'ellipsis' ? (
                              <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                            ) : (
                              <button
                                key={p}
                                className={`crm-pagination__num ${p === familyPage ? 'crm-pagination__num--active' : ''}`}
                                onClick={() => setFamilyPage(p)}
                              >
                                {p}
                              </button>
                            ),
                          )}
                          <button className="crm-pagination__btn" disabled={familyPage === totalPages} onClick={() => setFamilyPage((p) => p + 1)}>
                            NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mobile card layout */}
                  {isMobile && (
                    <>
                      {familiesLoading ? (
                        <div style={{ padding: 20 }}>
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <IonSkeletonText animated style={{ width: '50%', height: 16 }} />
                              <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <IonList lines="full">
                          {families.map((family: Family) => {
                            const noCoords = !family.latitude || !family.longitude;
                            const hasPending = pendingIds.has(family.id);
                            const hasIssue = noCoords || hasPending;
                            return (
                            <IonItem
                              key={family.id}
                              button
                              detail
                              onClick={() => router.push(`/families/${family.id}`)}
                            >
                              <IonAvatar slot="start" style={{ width: 44, height: 44 }}>
                                <div
                                  style={{
                                    width: '100%', height: '100%', borderRadius: '50%',
                                    backgroundColor: `${color}20`, color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 18, fontWeight: 600,
                                  }}
                                >
                                  {(family.name[0] ?? '').toUpperCase()}
                                </div>
                              </IonAvatar>
                              <IonLabel>
                                <h2 style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {family.display_name ?? family.name}
                                  {hasIssue && (
                                    <IonIcon icon={warningOutline} style={{ fontSize: 14, color: noCoords ? '#dc2626' : '#d97706' }} />
                                  )}
                                </h2>
                                <p style={{ fontSize: 13 }}>
                                  {[family.address, family.city].filter(Boolean).join(', ') || 'Fără adresă'}
                                </p>
                              </IonLabel>
                              <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <IonIcon icon={peopleOutline} color="medium" />
                                <IonBadge color="medium" style={{ fontSize: 12 }}>
                                  {family.member_count ?? family.members?.length ?? 0}
                                </IonBadge>
                              </div>
                            </IonItem>
                            );
                          })}
                        </IonList>
                      )}

                      {!familiesLoading && families.length === 0 && (
                        <div className="crm-empty-state">
                          <div className="crm-empty-state__icon"><IonIcon icon={home} /></div>
                          <h3 className="crm-empty-state__title">Nicio familie</h3>
                          <p className="crm-empty-state__text">Acest sector nu are familii alocate.</p>
                        </div>
                      )}

                      {totalPages > 1 && (
                        <div className="crm-pagination" style={{ paddingBottom: 80 }}>
                          <button className="crm-pagination__btn" disabled={familyPage === 1} onClick={() => setFamilyPage((p) => p - 1)}>
                            <IonIcon icon={chevronBack} style={{ fontSize: 12 }} />
                          </button>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', padding: '0 8px' }}>
                            {familyPage} / {totalPages}
                          </span>
                          <button className="crm-pagination__btn" disabled={familyPage === totalPages} onClick={() => setFamilyPage((p) => p + 1)}>
                            <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Grouped members tab */}
              {activeTab === 'grouped' && (
                <SectorMembersGrouped sectorId={id} sectorColor={color} />
              )}
            </>
          ) : (
            <div className="crm-empty-state">
              <div className="crm-empty-state__icon"><IonIcon icon={map} /></div>
              <h3 className="crm-empty-state__title">Sector negasit</h3>
              <p className="crm-empty-state__text">Sectorul solicitat nu a fost gasit.</p>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SectorDetailPage;
